/* eslint-disable @typescript-eslint/no-shadow */
import { useMemo, useEffect, useRef } from 'react'

import { useHandler } from './useHandler'

export * from './useUniqId'
export * from './useOnRefTargetClick'
export * from './useOnClickOutside'
export * from './useNumberMask'
export * from './useSelectableIds'
export * from './useHostnameRootUrl'
export * from './useElementDimensions'
export * from './useOnDialogBlur'
export * from './useBodyScrollLock'
export * from './useInfiniteScrollContainer'
export * from './useIsElementStuck'
export * from './useDeepCompareEffect'
export * from './useDynamicTextAreaHeight'

export { useHandler }

/**
 * Extracts argument types from a function
 */
type ExtractArgs<T extends (...args: any[]) => any> = T extends (
  ...args: infer A
) => any
  ? A
  : never

/**
 * Extracts return type from a function
 */
type ExtractReturn<T extends (...args: any[]) => any> = T extends (
  ...args: any[]
) => infer R
  ? R
  : never

/**
 * Calls a function and returns (memoizes) a result of the call with given arguments in an array
 *
 * Automatically invalidates the result if arguments or function changes
 */
export function useMemoizedCall<T extends (...args: any[]) => any>(
  fn: T,
  args: ExtractArgs<T>,
): ExtractReturn<T> {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => fn(...args), [fn, ...args])
}

/**
 * Creates an effect from static effect creator with arguments provided in an array
 */
export function useStaticEffect<T extends (...args: any[]) => any>(
  effectCreator: T,
  args: ExtractArgs<T>,
) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useEffect(() => effectCreator(...args), args)
}

/**
 * Return functions which holds flag if the component is or isnt mounted
 */
export const useIsMounted = (): (() => boolean) => {
  const isMountedRef = useRef(false)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  })

  return useHandler(() => isMountedRef.current)
}

/**
 * Returns a constant value, which has always the same reference.
 * You cannot change the value passed to the hook.
 *
 * Example usage: `const actions = useConstant([LOAD_A, LOAD_B])`
 */
// eslint-disable-next-line react-hooks/exhaustive-deps
export const useConstant = <T>(value: T): T => useMemo(() => value, [])
